import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useMutation } from '@tanstack/react-query';
import { IDNow2, type IdNowErrorKeys, type IdNowStatus } from '@repo/kyc';
import { useToast } from '@repo/design-system-kit';
import { useSentry } from '@repo/shared-kit/sentry';
import { type ResponseError } from '@repo/shared-kit/utils';
import {
  kycMutations,
  KycProcessStatus,
  KycProvider,
} from '@repo/api-kit/queries/due-diligence/kyc';
import { useKycContext } from '../context';
import { useKycInfo } from '../hooks';
import { getIdnowProviderDetails } from '../utils';
import { TOAST_TIMEOUT } from '../../constants';
import { KycError } from '../error';
import { SEND_SMS_TEMPLATE } from '../constants';
import styles from './idnow-provider.module.css';

export function IdnowProvider() {
  const intl = useIntl();
  const toasts = useToast();
  const sentry = useSentry();
  const {
    hasGermanResidencePermit,
    onBack,
    onComplete,
    membershipId,
    legalCountry,
    isRegisterFlow,
  } = useKycContext();
  const market = isRegisterFlow ? legalCountry : undefined;
  const { data: kycInfo, refetch, isLoading } = useKycInfo(market);
  const cancelled = useRef(false);

  const sendKycSms = useMutation({
    ...kycMutations.sendVerificationSms(),
    onError: (error: ResponseError) => {
      if (error.status === 429) {
        return toasts.showToast(
          {
            type: 'error',
            text: intl.formatMessage({
              id: 'due-diligence-kit.kyc.toasts.download-link-sms.rate-limit',
              defaultMessage:
                "We've already sent you an SMS with a link. If you haven't received it, try again in a few minutes.",
            }),
          },
          {
            timeout: TOAST_TIMEOUT,
          }
        );
      }

      return toasts.showToast(
        {
          type: 'error',
          text: intl.formatMessage({
            id: 'due-diligence-kit.kyc.toasts.download-link-sms.error',
            defaultMessage:
              'We encountered a technical issue while sending you the SMS. Could you try again?',
          }),
        },
        {
          timeout: TOAST_TIMEOUT,
        }
      );
    },
    onSuccess: () => {
      return toasts.showToast(
        {
          type: 'success',
          text: intl.formatMessage({
            id: 'due-diligence-kit.kyc.toasts.download-link-sms.success',
            defaultMessage: 'We sent you an SMS with a link.',
          }),
        },
        {
          timeout: TOAST_TIMEOUT,
        }
      );
    },
  });

  const providerDetails = getIdnowProviderDetails(kycInfo);
  const processStatus = kycInfo?.activeProcess?.processStatus;

  const handleSendSMS = useCallback(async () => {
    if (!providerDetails?.token) {
      return;
    }

    await sendKycSms.mutateAsync({
      redoToken: providerDetails.token,
      templateName: SEND_SMS_TEMPLATE.IDNOW_APP_LINK,
    });
  }, [providerDetails?.token, sendKycSms]);

  useEffect(() => {
    return () => {
      cancelled.current = true;
    };
  }, []);

  useEffect(() => {
    if (processStatus === KycProcessStatus.submitted && !cancelled.current) {
      onComplete(KycProvider.idnow);
    }
  }, [processStatus, onComplete]);

  if (isLoading) return null;

  if (processStatus === KycProcessStatus.submitted) return null;

  if (!providerDetails) {
    sentry.captureException(new Error('IDnow provider details are null'), {
      extra: {
        kycInfo,
        membershipId,
      },
      tags: {
        component: 'IdnowProvider',
        provider: 'idnow',
      },
    });

    return <KycError onRetry={refetch} />;
  }

  const { token, reason, status } = providerDetails;

  if (!token) {
    sentry.captureException(new Error('IDnow KYC token missing'), {
      extra: {
        kycInfo,
        membershipId,
      },
      tags: {
        component: 'IdnowProvider',
        provider: 'idnow',
      },
    });

    return <KycError onRetry={refetch} />;
  }

  const qrCodeUrl = `${window.location.origin}/download-idnow2?type=qr-code&id=${token}`;

  return (
    <div className={styles.idnow2}>
      <IDNow2
        hasGermanResidencePermit={hasGermanResidencePermit}
        onBack={onBack}
        onSendSMS={handleSendSMS}
        qrCodeUrl={qrCodeUrl}
        // oxlint-disable-next-line typescript/consistent-type-assertions
        reason={reason as IdNowErrorKeys}
        // oxlint-disable-next-line typescript/consistent-type-assertions
        status={status as IdNowStatus}
        token={token}
        tryAgain={refetch}
      />
    </div>
  );
}
